import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, FileText, ArrowLeft } from 'lucide-react';
import { LandingNavbar } from '../landing/LandingNavbar';
import { LandingFooter } from '../landing/LandingFooter';

interface PrivacyPolicyProps {
    onBack: () => void;
    onGetStarted: () => void;
}

export const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack, onGetStarted }) => {
    useEffect(() => {
        document.body.style.overflow = 'auto';
        window.scrollTo(0, 0);
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    const sections = [
        {
            title: "Data We Collect",
            icon: <Eye className="w-8 h-8 text-indigo-500" />,
            content: "We collect information you provide directly to us, such as when you create an account, create a board, or communicate with us. This include your name, email address, and any content you upload to our infinite canvas."
        },
        {
            title: "How We Use Data",
            icon: <FileText className="w-8 h-8 text-indigo-500" />,
            content: "Your data is used to provide, maintain, and improve NoteSpace. We use Vicra AI to process your notes locally or via secure encrypted channels to provide summaries and insights. We never sell your personal data to third parties."
        },
        {
            title: "Security & Encryption",
            icon: <Lock className="w-8 h-8 text-indigo-500" />,
            content: "We employ industry-standard security measures to protect your information. All data transmitted between your device and our servers is encrypted using TLS, and your notes are stored using high-grade encryption at rest via Supabase."
        }
    ];

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200">
            <LandingNavbar onGetStarted={onGetStarted} />

            <div className="pt-28 pb-10 px-6 max-w-5xl mx-auto border-b border-slate-900">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-xs font-black text-slate-500 hover:text-white transition-colors uppercase tracking-[0.2em]"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Hub
                </button>
            </div>

            <main className="max-w-5xl mx-auto px-6 py-20">
                <header className="mb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-4 text-indigo-500 text-xs font-black uppercase tracking-[0.3em] mb-8"
                    >
                        <Shield className="w-5 h-5" />
                        Trust & Safety
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="text-6xl md:text-8xl font-black text-white tracking-tighter mb-10"
                    >
                        Privacy <br /><span className="text-slate-500">Policy.</span>
                    </motion.h1>
                    <p className="text-2xl text-slate-400 font-medium leading-relaxed max-w-3xl">
                        At NoteSpace, we believe your ideas are your most valuable asset. Our privacy policy is designed to give you full control and transparency over your digital workspace.
                    </p>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-32">
                    {sections.map((s, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.1 }}
                            className="p-10 bg-slate-900 border-2 border-transparent hover:border-indigo-500/50 rounded-4xl transition-all group"
                        >
                            <div className="mb-8 group-hover:scale-110 transition-transform origin-left">
                                {s.icon}
                            </div>
                            <h3 className="text-3xl font-black text-white mb-4 tracking-tight">{s.title}</h3>
                            <p className="text-slate-400 font-medium leading-relaxed">
                                {s.content}
                            </p>
                        </motion.div>
                    ))}
                </div>

                <article className="prose prose-invert prose-slate max-w-none space-y-16">
                    <section className="space-y-6">
                        <h2 className="text-4xl font-black text-white tracking-tight">1. Information Collection</h2>
                        <div className="h-1 w-20 bg-indigo-500 mb-8" />
                        <p className="text-xl text-slate-300 leading-relaxed font-medium">
                            We collect information when you register for an account, participate in any interactive features of the Services, fill out a form, or otherwise communicate with us. The types of information we may collect include your name, email address, password, and any other information you choose to provide.
                        </p>
                    </section>

                    <section className="space-y-6">
                        <h2 className="text-4xl font-black text-white tracking-tight">2. Use of AI (Vicra AI)</h2>
                        <div className="h-1 w-20 bg-indigo-500 mb-8" />
                        <p className="text-xl text-slate-300 leading-relaxed font-medium">
                            Vicra AI processes your notes to provide intelligent insights, summaries, and diagram generation. We do not use your private notes to train our global models without your explicit opt-in preference. Your data remains yours.
                        </p>
                    </section>

                    <section className="space-y-6">
                        <h2 className="text-4xl font-black text-white tracking-tight">3. Data Retention</h2>
                        <div className="h-1 w-20 bg-indigo-500 mb-8" />
                        <p className="text-xl text-slate-300 leading-relaxed font-medium">
                            We store the information we collect about you for as long as is necessary for the purposes for which we originally collected it. You can delete your boards at any time, which moves them to the trash for 30 days before permanent deletion.
                        </p>
                    </section>

                    <section className="mt-32 p-16 bg-slate-900 rounded-5xl text-center border-2 border-slate-800">
                        <h3 className="text-4xl font-black text-white mb-6 tracking-tight">Have questions?</h3>
                        <p className="text-xl text-slate-500 font-medium mb-10">Our legal and technical teams are here to ensure your data stays safe.</p>
                        <a
                            href="mailto:privacy@notespace.io"
                            className="inline-block px-12 py-6 bg-white text-slate-950 rounded-3xl font-black text-xl hover:bg-slate-200 transition-all shadow-2xl"
                        >
                            Contact Privacy Team
                        </a>
                    </section>
                </article>
            </main>

            <LandingFooter />
        </div>
    );
};
